import _get from 'lodash/get';
import _map from 'lodash/map';
import _filter from 'lodash/filter';
import _includes from 'lodash/includes';
import _isEqual from 'lodash/isEqual';
import _kebabCase from 'lodash/kebabCase';
import _find from 'lodash/find';
import _isString from 'lodash/isString';
import { useObserver } from 'mobx-react-lite';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { parseProduct } from 'utils/helpers/product';
import { queryParamWeightToGqlFilterValue } from 'utils/helpers/product/weight-filter-converters';
import { determinePlatformType, mapOrderTypeToPreOrderType } from 'shared/helpers/enum-mappings';

import { CategoryPotencyRanges, CategoryPotencyUnits, POTENCY_UNITS, StrainTypeFilterOptions } from 'shared/constants';
import { FilteredProductsDocument } from 'src/gql/graphql';

import { getCategory, useDispensaryCategory } from 'src/hooks/use-dispensary-category';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import { useDispensaryMenuOrder } from 'src/dispensary/hooks/use-dispensary-menu-order';
import useUI from 'hooks/use-ui';
import useProductQueryParams from 'hooks/use-product-query-params';
import useMenuFilterOptions from 'hooks/use-menu-filter-options';
import usePriceRangeBands from 'hooks/use-price-range-bands';
import useCart from 'hooks/use-cart';
import { useDispensarySubcategory } from 'hooks/use-dispensary-subcategory';
import { useFilterProductsQueryByEffects } from 'src/hooks/use-filter-products-query-by-effects';
import { useFilterProductsQueryByTags } from 'src/hooks/use-filter-products-query-by-tags';
import { useSortProductsByCustomBrandsort } from 'src/hooks/use-sort-products-by-custom-brandsort';
import { useQuery } from 'src/hooks/use-query';
import { useUserSegments } from 'hooks/use-user-segments';

export const sortQueryParamToFields = (param, isMenuV2Enabled) => {
  const formattedParam = _isString(param) ? param.toLowerCase() : param;

  switch (formattedParam) {
    case 'pricehightolow':
      return { sortDirection: -1, sortBy: 'price' };
    case 'pricelowtohigh':
      return { sortDirection: 1, sortBy: 'price' };
    case 'potencyhightolow':
      return { sortDirection: -1, sortBy: 'potency' };
    case 'potencylowtohigh':
      return { sortDirection: 1, sortBy: 'potency' };
    case 'terpeneshightolow':
      return { sortDirection: -1, sortBy: 'terpenes' };
    case 'brand': // TODO: deprecate with FF: ecomm.admin.menu-v2.rollout
      return { sortDirection: 1, sortBy: isMenuV2Enabled ? 'featuredBrands' : 'brand' };
    case 'custom': // TODO: deprecate with FF: ecomm.admin.menu-v2.rollout
      return { sortDirection: 1, sortBy: 'weight' };
    case 'category':
      return { sortDirection: 1, sortBy: 'type' };
    case 'recentlyadded':
      // Newest first. Previously relied on the API always forcing this
      // direction; now explicit so the API can honor a real direction toggle
      // for other callers (ECOMM-13292).
      return { sortDirection: -1, sortBy: 'recentlyadded' };
    default:
      return { sortDirection: 1, sortBy: param };
  }
};

export function getProductsVariables({
  dispensaryId,
  pricingType,
  isKiosk,
  UI,
  preOrderType,
  userSegments = [],
  types = [],
  excludedTypes,
  option,
  brandIds,
  excludedBrandIds,
  terpeneIds,
  query,
  strainTypes,
  excludedStrainTypes,
  subcategories,
  THCContent,
  CBDContent,
  isOnSpecial,
  specialId,
  specialIds,
  specialType,
  productIds,
  staffPicks,
  useCache = false,
  isDefaultSort = false,
  sortBy,
  sortDirection,
  collectionId,
  ignoreQuantityThresholds = false,
  priceRange,
  page = 1,
  perPage = 50,
} = {}) {
  const productsFilter = {
    brandIds,
    excludedBrandIds,
    productIds,
    staffPicks,
    dispensaryId,
    option,
    pricingType,
    strainTypes,
    excludedStrainTypes,
    subcategories,
    terpenes: terpeneIds,
    Status: `Active`,
    CBDContent,
    THCContent,
    isOnSpecial,
    specialId,
    specialIds,
    specialType,
    types,
    excludedTypes,
    query,
    useCache,
    isDefaultSort,
    sortBy,
    sortDirection,
    bypassOnlineThresholds: isKiosk,
    ignoreQuantityThresholds,
    isKioskMenu: isKiosk,
    removeProductsBelowOptionThresholds: true,
    collectionId,
    userSegmentIds: userSegments.length > 0 ? userSegments : undefined,
    platformType: determinePlatformType(UI),
    preOrderType,
    priceRange,
  };

  return {
    productsFilter,
    page: (page || 1) - 1,
    perPage: perPage > 0 ? perPage : undefined,
  };
}

export function getPotencyContentForFilter(range, category) {
  const unit = CategoryPotencyUnits[category?.key] === `%` ? POTENCY_UNITS.PERCENTAGE : POTENCY_UNITS.MILLIGRAMS;
  if (range?.length === 2 && category?.key) {
    // If this is the default range for the category then don't apply any filtering
    if (_isEqual(CategoryPotencyRanges[category.key], range)) {
      return undefined;
    }
    // If the upper query param value is the max limit for the category,
    // include products with potencies that surpass the maximum.
    // e.g. if a pre-roll's potency is 60%, it should still show if the selected
    // potency filter range is [20, 50] even though the max range for pre-rolls is 50%.
    if (range[1] === CategoryPotencyRanges[category.key]?.[1]) {
      return { range: [range[0], Number.MAX_SAFE_INTEGER], unit };
    }
    return { range, unit };
  }
  return undefined;
}

/**
 * @param {Object} props
 * @param {Object} [props.filters]
 * @param {boolean} [props.filters.ignoreQuantityThresholds] - Show true POS quantity, bypassing
 *   Quantity Thresholds entirely. Used by TV Menu Boards (passive display, not an ordering flow).
 * @param {boolean} [props.skip]
 * @param {number} [props.perPage]
 * @returns {Object}
 */
export default function useDispensaryProducts(props) {
  const { filters = {}, perPage = 50, page } = props || {};
  let { skip = false } = props || {};
  const ignoreQuantityThresholds = filters?.ignoreQuantityThresholds ?? false;
  const { queryParams: productQueryParams } = useProductQueryParams();
  const { brands: loadedBrands, terpenes: loadedTerpenes, loading: menuFilterOptionsLoading } = useMenuFilterOptions({
    skipCurrentFilters: true,
    location: 'use-dispensary-products.js',
  });
  const { bands: priceRangeBands } = usePriceRangeBands();
  const category = useDispensaryCategory();
  const { subcategory, loading: subcategoryLoading } = useDispensarySubcategory();
  const { dispensary } = useDispensary();
  const cart = useCart();
  const UI = useUI();
  const isKiosk = UI?.isKiosk;
  const menuType = useObserver(() => cart.menuType);
  const flags = useFlags();
  const shouldDisableSearch = flags['ecomm.configuration.disable-product-search'] ?? false;
  const isMenuV2Enabled = flags['ecomm.admin.menu-v2.rollout'] ?? false;

  /**
   * Fetch user segments for exclusive collections filtering.
   *
   * Different segment values in variables create different cache keys, so users with
   * different segments automatically get different cached responses.
   *
   * Server validates client-provided segments to prevent spoofing.
   */
  const { segments: userSegments } = useUserSegments({
    dispensaryId: dispensary?.id,
  });

  if (shouldDisableSearch) {
    delete productQueryParams.search;
  }

  const {
    search: query,
    weight,
    brands: selectedBrands,
    page: pageFromQueryParams,
    straintypes,
    subcategories: subcategoriesFromQueryParams,
    category: categoryFromParams,
    effects: effectsFromParams,
    terpenes: terpenesFromParams,
    tags: tagsFromParams,
    pricerange: priceRangeIdFromParams,
  } = productQueryParams;

  // Query-param band id -> {min, max}. filters.priceRange overrides, same as filters.THCContent.
  const selectedPriceBand = filters?.priceRange
    ? undefined
    : _find(priceRangeBands, { bandId: priceRangeIdFromParams });
  const priceRangeForFilter =
    filters?.priceRange ?? (selectedPriceBand ? { min: selectedPriceBand.min, max: selectedPriceBand.max } : undefined);

  const option = _isString(weight) ? queryParamWeightToGqlFilterValue(weight) : weight;

  const strainTypesForFilter = _map(straintypes, (queryValue) => _find(StrainTypeFilterOptions, { queryValue })?.value);

  const thcContentForFilter = getPotencyContentForFilter(productQueryParams.potencythc, category);
  const cbdContentForFilter = getPotencyContentForFilter(productQueryParams.potencycbd, category);
  // Board columns pass an explicit THC range instead of relying on URL query params -- same
  // override pattern as filters?.types / filters?.strainTypes below.
  const THCContent = filters?.THCContent ?? thcContentForFilter;

  let types = [];

  if (filters?.category) {
    types = [filters.category];
  } else {
    types = query || !category || (category && category.key === 'all') ? [] : [category.value];
  }

  if (filters?.types?.length > 0) {
    types = filters.types;
  }

  if (categoryFromParams) {
    types = [getCategory(categoryFromParams).value];
  }

  let brandIds = filters?.brandIds;
  let excludedBrandIds = filters?.excludedBrandIds;
  const filterableBrands = filters?.brands || selectedBrands;
  const excludedFilterableBrands = filters?.excludedBrands;
  const resolveBrandIds = (names) =>
    _map(
      _filter(loadedBrands, (brand) => _includes(names, _kebabCase(brand.name))),
      (brand) => brand?.id
    );

  if (filterableBrands?.length || excludedFilterableBrands?.length) {
    skip = !loadedBrands?.length;
  }
  if (filterableBrands?.length) {
    // Name matching intentionally returns every duplicate brand ID.
    brandIds = resolveBrandIds(filterableBrands);
  }
  if (excludedFilterableBrands?.length) {
    excludedBrandIds = resolveBrandIds(excludedFilterableBrands);
  }

  let terpeneIds = filters?.terpenes;
  const filterableTerpenes = filters?.terpenes || terpenesFromParams;

  if (filterableTerpenes?.length) {
    // get all terpene objects that match terpene names from filter
    const matchingTerpenes = _filter(loadedTerpenes, (terpene) =>
      _includes(filterableTerpenes, _kebabCase(terpene.name))
    );

    skip = !loadedTerpenes?.length;
    terpeneIds = _map(matchingTerpenes, (terpene) => terpene?.compound_id);
  }

  // When no sorting option is selected, but a query is made we should sort by match score
  // Otherwise, default to the dispensary's default sort order
  let qpSortBy = productQueryParams.sortby;
  const isDefaultSort = qpSortBy === 'default';
  const categoryMenuOrder = useDispensaryMenuOrder(types[0]);

  if (isDefaultSort) {
    if (query) {
      qpSortBy = null;
    } else {
      qpSortBy = categoryMenuOrder;
    }
  }

  const { sortBy, sortDirection } = sortQueryParamToFields(filters.sortBy ?? qpSortBy, isMenuV2Enabled);

  const variables = getProductsVariables({
    dispensaryId: dispensary?.id,
    pricingType: menuType,
    isKiosk,
    UI,
    preOrderType: mapOrderTypeToPreOrderType(cart.simplifiedOrderType),
    userSegments,
    types,
    excludedTypes: filters?.excludedTypes,
    option,
    brandIds,
    excludedBrandIds,
    terpeneIds,
    query,
    strainTypes: filters?.strainTypes || strainTypesForFilter,
    excludedStrainTypes: filters?.excludedStrainTypes,
    subcategories: filters?.subcategories || subcategory || subcategoriesFromQueryParams,
    THCContent,
    CBDContent: cbdContentForFilter,
    isOnSpecial: filters?.isOnSpecial,
    specialId: filters?.specialId,
    specialIds: filters?.specialIds,
    specialType: filters?.specialType,
    productIds: filters?.productIds,
    staffPicks: filters?.staffPicks,
    useCache: filters?.useCache || false,
    isDefaultSort,
    sortBy,
    sortDirection,
    collectionId: filters?.collectionId,
    ignoreQuantityThresholds,
    priceRange: priceRangeForFilter,
    page: page || pageFromQueryParams,
    perPage,
  });

  const { data, loading: filteredProductsLoading, ...apolloValues } = useQuery(FilteredProductsDocument, {
    skip: !dispensary || skip,
    fetchPolicy: 'cache-first',
    variables,
  });
  const hasProducts = _get(data, 'filteredProducts.products.length', 0) > 0;
  const loading = hasProducts
    ? filteredProductsLoading
    : menuFilterOptionsLoading || subcategoryLoading || filteredProductsLoading;

  const parsedProducts = _map(_get(data, 'filteredProducts.products', []), parseProduct);
  const totalCountFromQuery = _get(data, 'filteredProducts.queryInfo.totalCount', 0);
  const totalPagesFromQuery = _get(data, 'filteredProducts.queryInfo.totalPages');

  const {
    products: filteredByEffectsProducts,
    totalCount: totalCountAfterEffects,
    totalPages: totalPagesAfterEffects,
  } = useFilterProductsQueryByEffects({
    effectsFromParams,
    perPage,
    products: parsedProducts,
    totalCount: totalCountFromQuery,
    totalPages: totalPagesFromQuery,
  });

  const { products: filteredByTagsProducts, totalCount, totalPages } = useFilterProductsQueryByTags({
    tagsFromParams: filters?.skipTagsFilter ? [] : tagsFromParams,
    perPage,
    products: filteredByEffectsProducts,
    totalCount: totalCountAfterEffects,
    totalPages: totalPagesAfterEffects,
  });

  const { products } = useSortProductsByCustomBrandsort({
    products: filteredByTagsProducts,
    dispensary,
    isDefaultSort,
    types,
  });

  return {
    products,
    totalCount,
    totalPages,
    data,
    loading,
    ...apolloValues,
  };
}
